import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

import { T } from './theme';
const API = 'https://tcio-api.onrender.com';
const card = { background:T.surface, border:`1px solid ${T.b200}`, borderRadius:10,
  boxShadow:'0 1px 2px rgba(110,59,71,.06), 0 4px 14px rgba(110,59,71,.07)' };
const label = { fontSize:10, letterSpacing:'1.1px', textTransform:'uppercase', color:T.inkSoft, fontWeight:600 };
const btn = (primary) => ({ borderRadius:8, padding:'9px 16px', font:'inherit', fontWeight:600,
  fontSize:13, cursor:'pointer', background: primary ? T.rose : '#fff',
  color: primary ? '#fff' : T.rose, border: primary ? 'none' : `1px solid ${T.b300}` });
const pill = (bg, fg, text) => <span style={{ background:bg, color:fg, borderRadius:99,
  padding:'2px 10px', fontSize:11.5, fontWeight:600, whiteSpace:'nowrap' }}>{text}</span>;
const inp = { font:'inherit', fontSize:13, padding:'8px 10px', borderRadius:8,
  border:`1px solid ${T.b300}`, color:T.ink, background:'#fff' };

const ORDER_PILL = {
  active:        () => pill(T.passBg, T.pass, 'Active'),
  pending_review:() => pill(T.warningBg, T.warning, 'Pending Review'),
  discontinued:  () => pill(T.b100, T.inkSoft, 'Discontinued'),
  completed:     () => pill(T.infoBg, T.info, 'Completed'),
};
const REQ_PILL = {
  collected: () => pill(T.infoBg, T.info, 'Collected'),
  sent:      () => pill(T.warningBg, T.warning, 'Sent to Lab'),
  resulted:  () => pill(T.passBg, T.pass, 'Resulted'),
  voided:    () => pill(T.b100, T.inkSoft, 'Voided'),
};
const RES_PILL = {
  received:          () => pill(T.infoBg, T.info, 'Received'),
  final:             () => pill(T.blockingBg, T.blocking, 'Needs Provider Review'),
  provider_reviewed: () => pill(T.passBg, T.pass, 'Provider Reviewed'),
};

const fmtDT = (v) => v ? new Date(v).toLocaleString('en-US',
  { timeZone:'America/Los_Angeles', month:'short', day:'numeric', hour:'numeric', minute:'2-digit' }) : '—';
const fmtD = (v) => v ? new Date(v).toLocaleDateString('en-US',
  { timeZone:'America/Los_Angeles', month:'short', day:'numeric', year:'numeric' }) : '—';

export default function EMRLabs({ active, patientId, episodeId, facilityId }) {
  const [refData, setRefData] = useState({ labs: [], panels: [] });
  const [chain, setChain] = useState({ orders: [], needs_provider_review: 0 });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null); // {kind:'ok'|'err', text}
  const [busy, setBusy] = useState(false);

  // Order form
  const [panelId, setPanelId] = useState('');
  const [prescribers, setPrescribers] = useState([]);
  const [orderedBy, setOrderedBy] = useState('');
  useEffect(() => {
    axios.get(`${API}/api/emr/labs/prescribers`, { params: { facility_id: facilityId || undefined } })
      .then(r => setPrescribers(r.data.prescribers || [])).catch(() => setPrescribers([]));
  }, [facilityId]);
  useEffect(() => {
    if (!orderedBy && chain.orders && chain.orders[0] && chain.orders[0].ordered_by && prescribers.some(p => p.id === chain.orders[0].ordered_by)) setOrderedBy(chain.orders[0].ordered_by);
  }, [chain.orders, prescribers]); // eslint-disable-line react-hooks/exhaustive-deps
  const [onAdmission, setOnAdmission] = useState(false);
  const [necessity, setNecessity] = useState('');
  const [previewed, setPreviewed] = useState(false);

  // Collection form (per order)
  const [collectFor, setCollectFor] = useState(null); // order id
  const [specSource, setSpecSource] = useState('urine');
  const [openResults, setOpenResults] = useState({}); // result id -> bool

  const say = (kind, text) => { setMsg({ kind, text }); setTimeout(() => setMsg(null), 6000); };
  // NPI prompt (ticket 3b1f75e1): if the chosen provider has no NPI on file, ask for it right here and save it to their record.
  const ensureNpi = async (providerId) => {
    const p = prescribers.find(x => x.id === providerId); if (!p || p.has_npi) return true;
    const npi = window.prompt(`${p.name} has no NPI on file, so the lab cannot receive this order electronically.\n\nEnter ${p.name}'s 10-digit NPI to save it to their record and continue:`, '');
    if (npi === null) return false;
    try { await axios.patch(`${API}/api/emr/labs/prescribers/${providerId}/npi`, { npi: npi.trim() });
      setPrescribers(ps => ps.map(x => x.id === providerId ? { ...x, has_npi: true } : x)); say('ok', 'NPI saved to ' + p.name + "'s record."); return true; }
    catch (e) { say('err', errText(e)); return false; }
  };
  const errText = (e) => (e.response && e.response.data && e.response.data.error) || e.message;

  const loadAll = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/api/emr/labs`),
        axios.get(`${API}/api/emr/labs/chain?patient_id=${patientId}`),
      ]);
      setRefData(r1.data);
      setChain(r2.data);
      if (!panelId && r1.data.panels.length) setPanelId(r1.data.panels[0].id);
    } catch (e) { say('err', 'Load failed: ' + errText(e)); }
    setLoading(false);
  }, [patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (active) loadAll(); }, [active, loadAll]);

  const preview = async () => {
    if (!panelId) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/emr/labs/necessity-preview`,
        { patient_id: patientId, episode_id: episodeId || null, panel_id: panelId });
      setNecessity(r.data.medical_necessity);
      setPreviewed(true);
    } catch (e) { say('err', 'Preview failed: ' + errText(e)); }
    setBusy(false);
  };

  const signOrder = async () => {
    if (!panelId) return;
    setBusy(true);
    try {
      if (orderedBy && !(await ensureNpi(orderedBy))) { setBusy(false); return; }
      const r = await axios.post(`${API}/api/emr/labs/orders`, { ordered_by: orderedBy || undefined,
        patient_id: patientId, episode_id: episodeId || null, facility_id: facilityId || null,
        lab_id: (refData.panels.find(p => p.id === panelId) || {}).lab_id || null,
        panel_id: panelId, on_admission: onAdmission,
        medical_necessity: previewed ? necessity : null,
      });
      const st = r.data.order.status;
      say('ok', st === 'active'
        ? `Order signed and active — ${r.data.test_count} tests.`
        : `Order placed — pending prescriber review (${r.data.test_count} tests).`);
      setNecessity(''); setPreviewed(false); setOnAdmission(false);
      loadAll();
    } catch (e) { say('err', 'Order failed: ' + errText(e)); }
    setBusy(false);
  };

  const reviewOrder = async (id) => {
    setBusy(true);
    try { await axios.post(`${API}/api/emr/labs/orders/${id}/review`); say('ok', 'Order reviewed and activated.'); loadAll(); }
    catch (e) { say('err', errText(e)); }
    setBusy(false);
  };

  const discontinue = async (id) => {
    const reason = window.prompt('Reason for discontinuing this lab order:');
    if (!reason || !reason.trim()) return;
    setBusy(true);
    try { await axios.post(`${API}/api/emr/labs/orders/${id}/discontinue`, { reason: reason.trim() }); say('ok', 'Order discontinued.'); loadAll(); }
    catch (e) { say('err', errText(e)); }
    setBusy(false);
  };

  const collect = async (orderId) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/emr/labs/requisitions`,
        { order_id: orderId, specimen_source: specSource });
      say('ok', `Specimen collected — ID ${r.data.requisition.specimen_id}`);
      setCollectFor(null);
      loadAll();
    } catch (e) { say('err', 'Collection failed: ' + errText(e)); }
    setBusy(false);
  };

  const sendReq = async (id) => {
    setBusy(true);
    try {
      try { await axios.post(`${API}/api/emr/labs/requisitions/${id}/send`); say('ok', 'Requisition sent to lab.'); loadAll(); return; }
      catch (e0) {
        const d = (e0.response && e0.response.data) || {};
        if (!d.no_npi) throw e0;
        // No NPI on the ordering provider: prompt for it (prescriber) or reassign the order to the right prescriber first.
        let pid = d.provider_id;
        if (!d.provider_is_prescriber) {
          const list = prescribers.map((x, i) => `${i + 1}. ${x.name}${x.credentials ? ', ' + x.credentials : ''}${x.has_npi ? '' : ' (no NPI on file)'}`).join('\n');
          const pick = window.prompt(`This order is recorded under a non-prescriber. Which prescriber ordered it?\n\n${list}\n\nEnter the number:`, '');
          if (pick === null) return;
          const sel = prescribers[Number(pick) - 1]; if (!sel) { say('err', 'No provider picked.'); return; }
          await axios.patch(`${API}/api/emr/labs/orders/${d.order_id}/provider`, { ordered_by: sel.id }); pid = sel.id;
        }
        if (!(await ensureNpi(pid))) return;
        await axios.post(`${API}/api/emr/labs/requisitions/${id}/send`); say('ok', 'Requisition sent to lab.'); loadAll();
      }
    } catch (e) { say('err', errText(e)); }
    finally { setBusy(false); }
  };

  const voidReq = async (id) => {
    const reason = window.prompt('Reason for voiding this requisition:');
    if (!reason || !reason.trim()) return;
    setBusy(true);
    try { await axios.post(`${API}/api/emr/labs/requisitions/${id}/void`, { reason: reason.trim() }); say('ok', 'Requisition voided.'); loadAll(); }
    catch (e) { say('err', errText(e)); }
    setBusy(false);
  };

  const reviewResult = async (id) => {
    setBusy(true);
    try { await axios.post(`${API}/api/emr/labs/results/${id}/review`); say('ok', 'Result marked provider-reviewed.'); loadAll(); }
    catch (e) { say('err', errText(e)); }
    setBusy(false);
  };

  const selPanel = refData.panels.find(p => p.id === panelId);

  return (
    <div>
      {msg && (
        <div style={{ ...card, padding:'10px 14px', marginBottom:12, fontSize:13, fontWeight:600,
          background: msg.kind === 'ok' ? T.passBg : T.blockingBg,
          color: msg.kind === 'ok' ? T.pass : T.blocking,
          border:`1px solid ${msg.kind === 'ok' ? T.pass : T.blocking}` }}>
          {msg.text}
        </div>
      )}

      {chain.needs_provider_review > 0 && (
        <div style={{ ...card, padding:'10px 14px', marginBottom:12, background:T.blockingBg,
          border:`1px solid ${T.blocking}`, color:T.blocking, fontSize:13, fontWeight:600 }}>
          {chain.needs_provider_review} final result{chain.needs_provider_review > 1 ? 's' : ''} awaiting provider review
        </div>
      )}

      {/* ── ORDER PANEL ── */}
      <div style={{ ...card, padding:16, marginBottom:14 }}>
        <div style={{ ...label, marginBottom:10 }}>Order Lab Panel</div>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
          <select value={orderedBy} onChange={e => setOrderedBy(e.target.value)} style={{ marginRight: 8 }}>
                <option value="">Ordering provider (prescriber)…</option>
                {prescribers.map(p => <option key={p.id} value={p.id}>{p.name}{p.credentials ? ', ' + p.credentials : ''}{p.has_npi ? '' : ' (no NPI on file)'}</option>)}
              </select>
              <select style={{ ...inp, minWidth:280 }} value={panelId} onChange={e => { setPanelId(e.target.value); setPreviewed(false); setNecessity(''); }}>
            {refData.panels.map(p => (
              <option key={p.id} value={p.id}>{p.name} ({p.test_count} tests)</option>
            ))}
          </select>
          <label style={{ fontSize:13, color:T.ink, display:'flex', alignItems:'center', gap:6 }}>
            <input type="checkbox" checked={onAdmission} onChange={e => setOnAdmission(e.target.checked)} />
            On admission
          </label>
          <button style={btn(false)} disabled={busy || !panelId} onClick={preview}>Preview Necessity</button>
          <button style={btn(true)} disabled={busy || !panelId} onClick={signOrder}>Sign &amp; Order</button>
        </div>
        {previewed && (
          <div style={{ marginTop:12 }}>
            <div style={{ ...label, marginBottom:6 }}>Generated Medical Necessity (editable)</div>
            <textarea style={{ ...inp, width:'100%', minHeight:90, boxSizing:'border-box', resize:'vertical' }}
              value={necessity} onChange={e => setNecessity(e.target.value)} />
          </div>
        )}
        {selPanel && !previewed && (
          <div style={{ marginTop:8, fontSize:12.5, color:T.inkSoft }}>
            Medical necessity will be generated from active diagnoses and medication orders at signing.
          </div>
        )}
      </div>

      {/* ── ORDERS + CHAIN ── */}
      {loading ? (
        <div style={{ color:T.inkSoft, fontSize:13, padding:12 }}>Loading lab chain…</div>
      ) : chain.orders.length === 0 ? (
        <div style={{ ...card, padding:20, color:T.inkSoft, fontSize:13 }}>No lab orders yet for this patient.</div>
      ) : chain.orders.map(o => (
        <div key={o.id} style={{ ...card, marginBottom:14, overflow:'hidden' }}>
          <div style={{ padding:'12px 16px', borderBottom:`1px solid ${T.b100}`,
            display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
            {(ORDER_PILL[o.status] || ORDER_PILL.completed)()}
            <strong style={{ fontSize:14, color:T.ink }}>{o.panel_name || 'Lab Order'}</strong>
            <span style={{ fontSize:12.5, color:T.inkSoft }}>
              {o.lab_name || ''} · {o.tests.length} tests · Ordered {fmtD(o.created_at)} by {o.ordered_by_name || '—'}
              {o.on_admission ? ' · On admission' : ''}{o.on_discharge ? ' · On discharge' : ''}
            </span>
            <span style={{ flex:1 }} />
            {o.status === 'pending_review' && (
              <button style={btn(true)} disabled={busy} onClick={() => reviewOrder(o.id)}>Review &amp; Activate</button>
            )}
            {o.status === 'active' && (
              <>
                <button style={btn(true)} disabled={busy}
                  onClick={() => setCollectFor(collectFor === o.id ? null : o.id)}>Collect Specimen</button>
                <button style={btn(false)} disabled={busy} onClick={() => discontinue(o.id)}>Discontinue</button>
              </>
            )}
          </div>

          {o.medical_necessity && (
            <div style={{ padding:'10px 16px', borderBottom:`1px solid ${T.b100}`, fontSize:12.5,
              color:T.inkSoft, fontStyle:'italic' }}>
              {o.medical_necessity}
            </div>
          )}
          {o.status === 'discontinued' && (
            <div style={{ padding:'8px 16px', borderBottom:`1px solid ${T.b100}`, fontSize:12.5, color:T.inkSoft }}>
              Discontinued {fmtDT(o.discontinued_at)} by {o.discontinued_by_name || '—'} — {o.discontinue_reason}
            </div>
          )}

          {collectFor === o.id && (
            <div style={{ padding:'12px 16px', borderBottom:`1px solid ${T.b100}`, background:T.b100,
              display:'flex', gap:10, alignItems:'center' }}>
              <span style={label}>Specimen Source</span>
              <select style={inp} value={specSource} onChange={e => setSpecSource(e.target.value)}>
                <option value="urine">Urine</option>
                <option value="blood">Blood</option>
                <option value="oral">Oral</option>
                <option value="breath">Breath</option>
                <option value="other">Other</option>
              </select>
              <button style={btn(true)} disabled={busy} onClick={() => collect(o.id)}>Confirm Collection</button>
              <button style={btn(false)} onClick={() => setCollectFor(null)}>Cancel</button>
            </div>
          )}

          {o.requisitions.length > 0 && (
            <div style={{ padding:'8px 16px 14px' }}>
              {o.requisitions.map(rq => (
                <div key={rq.id} style={{ borderTop:`1px solid ${T.b100}`, paddingTop:10, marginTop:10 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                    {(REQ_PILL[rq.status] || REQ_PILL.collected)()}
                    <span style={{ fontSize:13, fontWeight:600, color:T.ink }}>{rq.specimen_id}</span>
                    <span style={{ fontSize:12.5, color:T.inkSoft }}>
                      {rq.specimen_source} · Collected {fmtDT(rq.collected_at)} by {rq.collected_by_name || '—'}
                      {rq.sent_at ? ` · Sent ${fmtDT(rq.sent_at)}` : ''}
                    </span>
                    <span style={{ flex:1 }} />
                    {rq.status === 'collected' && (
                      <>
                        <button style={btn(true)} disabled={busy} onClick={() => sendReq(rq.id)}>Send to Lab</button>
                        <button style={btn(false)} disabled={busy} onClick={() => voidReq(rq.id)}>Void</button>
                      </>
                    )}
                    {rq.status === 'sent' && (
                      <button style={btn(false)} disabled={busy} onClick={() => voidReq(rq.id)}>Void</button>
                    )}
                  </div>
                  {rq.status === 'voided' && (
                    <div style={{ fontSize:12, color:T.inkSoft, marginTop:4 }}>
                      Voided {fmtDT(rq.voided_at)} — {rq.void_reason}
                    </div>
                  )}

                  {rq.results.map(res => (
                    <div key={res.id} style={{ marginTop:8, marginLeft:8, borderLeft:`3px solid ${T.b200}`, paddingLeft:12 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                        {(RES_PILL[res.result_status] || RES_PILL.received)()}
                        <span style={{ fontSize:13, color:T.ink }}>{res.test_name}</span>
                        <span style={{ fontSize:12, color:T.inkSoft }}>
                          Received {fmtDT(res.received_at)}
                          {res.reviewed_at ? ` · Reviewed ${fmtDT(res.reviewed_at)} by ${res.reviewed_by_name || '—'}` : ''}
                        </span>
                        <span style={{ flex:1 }} />
                        <button style={btn(false)}
                          onClick={() => setOpenResults(s => ({ ...s, [res.id]: !s[res.id] }))}>
                          {openResults[res.id] ? 'Hide Values' : `Values (${res.values.length})`}
                        </button>
                        {res.result_status !== 'provider_reviewed' && (
                          <button style={btn(true)} disabled={busy} onClick={() => reviewResult(res.id)}>Mark Reviewed</button>
                        )}
                      </div>
                      {openResults[res.id] && res.values.length > 0 && (
                        <table style={{ width:'100%', borderCollapse:'collapse', marginTop:8, fontSize:12.5 }}>
                          <thead>
                            <tr>
                              {['Analyte','Result','Cutoff','Unit','Outcome'].map(h => (
                                <th key={h} style={{ ...label, textAlign:'left', padding:'4px 8px',
                                  borderBottom:`1px solid ${T.b200}` }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {res.values.map(v => (
                              <tr key={v.id} style={{
                                background: v.outcome === 'abnormal' ? T.blockingBg :
                                  v.outcome === 'invalid' ? T.warningBg : 'transparent' }}>
                                <td style={{ padding:'4px 8px', color:T.ink, fontWeight: v.outcome !== 'normal' ? 700 : 400 }}>{v.analyte}</td>
                                <td style={{ padding:'4px 8px', color: v.outcome === 'abnormal' ? T.blocking : T.ink,
                                  fontWeight: v.outcome !== 'normal' ? 700 : 400 }}>{v.observed_result}{v.value_status === 'corrected' && <span style={{ marginLeft: 8 }}>{pill(T.warningBg, T.warning, 'Corrected')}</span>}</td>
                                <td style={{ padding:'4px 8px', color:T.inkSoft }}>{v.cutoff_reference || '—'}</td>
                                <td style={{ padding:'4px 8px', color:T.inkSoft }}>{v.unit || '—'}</td>
                                <td style={{ padding:'4px 8px' }}>
                                  {v.outcome === 'abnormal' ? pill(T.blockingBg, T.blocking, 'Abnormal') :
                                   v.outcome === 'invalid' ? pill(T.warningBg, T.warning, 'Invalid') :
                                   pill(T.passBg, T.pass, 'Normal')}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
